<section class="relative flex flex-col items-center w-full min-w-0 overflow-x-hidden">
  <div class="relative flex flex-col w-full max-w-5xl gap-6 px-2 sm:px-3">
    <header class="space-y-2 min-w-0">
      <h1 class="text-xl sm:text-2xl font-bold text-gray-800">Attestations & identifiants</h1>
      <p class="text-sm text-gray-600 max-w-3xl">
        Consultez et téléchargez vos documents officiels (URSSAF, impôts, KBIS). Configurez les identifiants des plateformes
        pour permettre la récupération automatique des pièces.
      </p>
    </header>

    <div id="identifiants-plateformes" class="flex flex-col bg-white rounded-md py-5 px-4 sm:py-6 sm:px-6 shadow-xl min-w-0">
      <div class="flex items-center gap-2 pb-4 border-b border-gray-100 mb-4">
        <lucide-icon [name]="FileText" size="20" class="text-gray-700"></lucide-icon>
        <h2 class="text-gray-800 text-lg font-semibold">Documents</h2>
      </div>

      <ul class="md:hidden divide-y divide-gray-100 rounded-lg border border-gray-200 overflow-hidden">
        @for (doc of documentStatuses(); track doc.name) {
          <li class="p-4 flex flex-col gap-3 bg-white">
            <div class="flex items-start gap-2 min-w-0">
              <lucide-icon [name]="FileText" size="16" class="shrink-0 mt-0.5" [class]="doc.available ? 'text-secondary' : 'text-gray-300'"></lucide-icon>
              <div class="min-w-0 flex-1">
                <p class="text-sm font-medium" [class]="doc.available ? 'text-gray-800' : 'text-gray-500'">{{ doc.name }}</p>
                <span class="inline-block mt-1 text-xs px-2 py-0.5 rounded font-medium"
                      [class]="doc.available ? 'bg-secondary/10 text-secondary' : 'bg-gray-100 text-gray-500'">
                  {{ doc.source }}
                </span>
              </div>
            </div>
            <div class="text-xs">
              @if (doc.available) {
                <div class="flex items-center gap-1.5 text-green-700">
                  <lucide-icon [name]="CheckCircle2" size="15"></lucide-icon>
                  <span class="font-medium">Disponible</span>
                  @if (doc.date) {
                    <span class="text-gray-400">· {{ doc.date | date: "dd/MM/yyyy HH:mm" }}</span>
                  }
                </div>
              } @else if (doc.credentialsConfigured && doc.unavailableMessage) {
                <div class="flex items-center gap-1.5 text-amber-600">
                  <lucide-icon [name]="Clock" size="15"></lucide-icon>
                  <span class="font-medium">{{ doc.unavailableMessage }}</span>
                </div>
              } @else if (doc.credentialsConfigured) {
                <div class="flex items-center gap-1.5 text-amber-600">
                  <lucide-icon [name]="Clock" size="15"></lucide-icon>
                  <span class="font-medium">Disponible dès demain</span>
                </div>
              } @else {
                <div class="flex items-center gap-1.5 text-gray-400">
                  <lucide-icon [name]="AlertCircle" size="15"></lucide-icon>
                  <span class="font-medium">Identifiants {{ doc.credentialSource }} requis</span>
                </div>
              }
            </div>
            @if (doc.available && doc.id) {
              <button type="button" (click)="downloadDocument(doc)"
                      class="inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-secondary/20 bg-secondary/5 px-3 py-2 text-sm text-secondary font-medium cursor-pointer">
                <lucide-icon [name]="Download" size="14"></lucide-icon>
                Télécharger
              </button>
            }
          </li>
        }
      </ul>

      <div class="hidden md:block overflow-x-auto rounded-lg border border-gray-200">
        <table class="w-full text-sm">
          <thead>
            <tr class="bg-gray-50 text-left text-gray-600">
              <th class="px-4 py-2.5 font-medium">Document</th>
              <th class="px-4 py-2.5 font-medium">Source</th>
              <th class="px-4 py-2.5 font-medium">Statut</th>
              <th class="px-4 py-2.5 font-medium">Date</th>
              <th class="px-4 py-2.5 font-medium text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            @for (doc of documentStatuses(); track doc.name) {
              <tr class="border-t border-gray-100 hover:bg-gray-50 transition-colors">
                <td class="px-4 py-3">
                  <div class="flex items-center gap-2">
                    <lucide-icon [name]="FileText" size="16" [class]="doc.available ? 'text-secondary' : 'text-gray-300'"></lucide-icon>
                    <span [class]="doc.available ? 'text-gray-800' : 'text-gray-500'">{{ doc.name }}</span>
                  </div>
                </td>
                <td class="px-4 py-3">
                  <span class="text-xs px-2 py-0.5 rounded font-medium"
                        [class]="doc.available ? 'bg-secondary/10 text-secondary' : 'bg-gray-100 text-gray-500'">
                    {{ doc.source }}
                  </span>
                </td>
                <td class="px-4 py-3">
                  @if (doc.available) {
                    <div class="flex items-center gap-1.5 text-green-700">
                      <lucide-icon [name]="CheckCircle2" size="15"></lucide-icon>
                      <span class="text-xs font-medium">Disponible</span>
                    </div>
                  } @else if (doc.credentialsConfigured && doc.unavailableMessage) {
                    <div class="flex items-center gap-1.5 text-amber-600">
                      <lucide-icon [name]="Clock" size="15"></lucide-icon>
                      <span class="text-xs font-medium">{{ doc.unavailableMessage }}</span>
                    </div>
                  } @else if (doc.credentialsConfigured) {
                    <div class="flex items-center gap-1.5 text-amber-600">
                      <lucide-icon [name]="Clock" size="15"></lucide-icon>
                      <span class="text-xs font-medium">Disponible dès demain</span>
                    </div>
                  } @else {
                    <div class="flex items-center gap-1.5 text-gray-400">
                      <lucide-icon [name]="AlertCircle" size="15"></lucide-icon>
                      <span class="text-xs font-medium">Identifiants {{ doc.credentialSource }} requis</span>
                    </div>
                  }
                </td>
                <td class="px-4 py-3 text-gray-500 whitespace-nowrap">
                  @if (doc.date) {
                    {{ doc.date | date: "dd/MM/yyyy HH:mm" }}
                  } @else {
                    <span class="text-gray-300">-</span>
                  }
                </td>
                <td class="px-4 py-3 text-right">
                  @if (doc.available && doc.id) {
                    <button type="button" (click)="downloadDocument(doc)"
                            class="inline-flex items-center gap-1.5 text-secondary hover:opacity-90 font-medium cursor-pointer">
                      <lucide-icon [name]="Download" size="14"></lucide-icon>
                      Télécharger
                    </button>
                  } @else {
                    <span class="text-gray-300 text-xs">-</span>
                  }
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </div>

    <div class="flex flex-col bg-white rounded-md py-5 px-4 sm:py-6 sm:px-6 shadow-xl min-w-0">
      <div class="flex items-center gap-2 pb-4 border-b border-gray-100 mb-4">
        <lucide-icon [name]="KeyRound" size="20" class="text-gray-700"></lucide-icon>
        <h2 class="text-gray-800 text-lg font-semibold">Identifiants des plateformes</h2>
      </div>

      <div class="flex flex-col gap-5 text-sm">
        <div class="flex flex-col gap-1.5">
          <div class="flex flex-wrap items-center gap-2">
            <span class="font-medium text-gray-700">URSSAF</span>
            @if (hasConfiguredCredentials('urssaf')) {
              <span class="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">Configuré</span>
              <button type="button" (click)="deleteCredentials('urssaf')" class="text-red-400 hover:text-red-600" title="Supprimer">
                <lucide-icon [name]="X" size="14"></lucide-icon>
              </button>
            } @else {
              <span class="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">Non configuré</span>
            }
          </div>
          <div class="flex flex-col sm:flex-row gap-2">
            <input type="text" placeholder="Identifiant" class="flex-1 min-w-0 border border-gray-300 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-secondary"
                   [value]="passwordForm().nomUtilisateurUrssaf ?? ''"
                   (input)="updatePasswordField('nomUtilisateurUrssaf', $any($event.target).value)" />
            <div class="relative flex-1 min-w-0">
              <input [type]="showPasswordUrssaf() ? 'text' : 'password'" placeholder="Mot de passe"
                     class="w-full border border-gray-300 rounded px-2.5 py-1.5 pr-8 text-sm focus:outline-none focus:ring-1 focus:ring-secondary"
                     [value]="passwordForm().passwordUrssaf ?? ''"
                     (input)="updatePasswordField('passwordUrssaf', $any($event.target).value)" />
              <button type="button" class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      (click)="showPasswordUrssaf.set(!showPasswordUrssaf())">
                <lucide-icon [name]="showPasswordUrssaf() ? EyeOff : Eye" size="14"></lucide-icon>
              </button>
            </div>
          </div>
        </div>

        <div class="flex flex-col gap-1.5">
          <div class="flex flex-wrap items-center gap-2">
            <span class="font-medium text-gray-700">Impots.gouv</span>
            @if (hasConfiguredCredentials('impots')) {
              <span class="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">Configuré</span>
              <button type="button" (click)="deleteCredentials('impots')" class="text-red-400 hover:text-red-600" title="Supprimer">
                <lucide-icon [name]="X" size="14"></lucide-icon>
              </button>
            } @else {
              <span class="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">Non configuré</span>
            }
          </div>
          <div class="flex flex-col sm:flex-row gap-2">
            <input type="text" placeholder="Identifiant" class="flex-1 min-w-0 border border-gray-300 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-secondary"
                   [value]="passwordForm().nomUtilisateurImpotsGouv ?? ''"
                   (input)="updatePasswordField('nomUtilisateurImpotsGouv', $any($event.target).value)" />
            <div class="relative flex-1 min-w-0">
              <input [type]="showPasswordImpots() ? 'text' : 'password'" placeholder="Mot de passe"
                     class="w-full border border-gray-300 rounded px-2.5 py-1.5 pr-8 text-sm focus:outline-none focus:ring-1 focus:ring-secondary"
                     [value]="passwordForm().passwordImpotsGouv ?? ''"
                     (input)="updatePasswordField('passwordImpotsGouv', $any($event.target).value)" />
              <button type="button" class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      (click)="showPasswordImpots.set(!showPasswordImpots())">
                <lucide-icon [name]="showPasswordImpots() ? EyeOff : Eye" size="14"></lucide-icon>
              </button>
            </div>
          </div>
        </div>

        <div class="flex flex-col gap-1.5">
          <div class="flex flex-wrap items-center gap-2">
            <span class="font-medium text-gray-700">Provigis</span>
            @if (hasConfiguredCredentials('provigis')) {
              <span class="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">Configuré</span>
              <button type="button" (click)="deleteCredentials('provigis')" class="text-red-400 hover:text-red-600" title="Supprimer">
                <lucide-icon [name]="X" size="14"></lucide-icon>
              </button>
            } @else {
              <span class="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">Non configuré</span>
            }
          </div>
          <div class="flex flex-col sm:flex-row gap-2">
            <input type="text" placeholder="Identifiant" class="flex-1 min-w-0 border border-gray-300 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-secondary"
                   [value]="passwordForm().nomUtilisateurProvigis ?? ''"
                   (input)="updatePasswordField('nomUtilisateurProvigis', $any($event.target).value)" />
            <div class="relative flex-1 min-w-0">
              <input [type]="showPasswordProvigis() ? 'text' : 'password'" placeholder="Mot de passe"
                     class="w-full border border-gray-300 rounded px-2.5 py-1.5 pr-8 text-sm focus:outline-none focus:ring-1 focus:ring-secondary"
                     [value]="passwordForm().passwordProvigis ?? ''"
                     (input)="updatePasswordField('passwordProvigis', $any($event.target).value)" />
              <button type="button" class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      (click)="showPasswordProvigis.set(!showPasswordProvigis())">
                <lucide-icon [name]="showPasswordProvigis() ? EyeOff : Eye" size="14"></lucide-icon>
              </button>
            </div>
          </div>
        </div>

        <div class="flex flex-col gap-1.5">
          <div class="flex flex-wrap items-center gap-2">
            <span class="font-medium text-gray-700">MonIdenum</span>
            @if (hasConfiguredCredentials('idenum')) {
              <span class="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">Configuré</span>
              <button type="button" (click)="deleteCredentials('idenum')" class="text-red-400 hover:text-red-600" title="Supprimer">
                <lucide-icon [name]="X" size="14"></lucide-icon>
              </button>
            } @else {
              <span class="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">Non configuré</span>
            }
          </div>
          <div class="flex flex-col sm:flex-row gap-2">
            <input type="text" placeholder="Identifiant" class="flex-1 min-w-0 border border-gray-300 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-secondary"
                   [value]="passwordForm().nomUtilisateurIdenum ?? ''"
                   (input)="updatePasswordField('nomUtilisateurIdenum', $any($event.target).value)" />
            <div class="relative flex-1 min-w-0">
              <input [type]="showPasswordIdenum() ? 'text' : 'password'" placeholder="Mot de passe"
                     class="w-full border border-gray-300 rounded px-2.5 py-1.5 pr-8 text-sm focus:outline-none focus:ring-1 focus:ring-secondary"
                     [value]="passwordForm().passwordIdenum ?? ''"
                     (input)="updatePasswordField('passwordIdenum', $any($event.target).value)" />
              <button type="button" class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      (click)="showPasswordIdenum.set(!showPasswordIdenum())">
                <lucide-icon [name]="showPasswordIdenum() ? EyeOff : Eye" size="14"></lucide-icon>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="flex justify-stretch sm:justify-end pt-6 mt-2 border-t border-gray-100">
        <button type="button" (click)="savePasswords()" [disabled]="savingPasswords()"
                class="flex w-full sm:w-auto items-center justify-center gap-2 bg-secondary hover:bg-secondary-hover disabled:bg-secondary/50 text-white text-sm font-medium px-4 py-2.5 sm:py-2 rounded-md cursor-pointer disabled:cursor-not-allowed">
          <lucide-icon [name]="Save" size="14"></lucide-icon>
          @if (savingPasswords()) { Enregistrement... } @else { Enregistrer les identifiants }
        </button>
      </div>
    </div>
  </div>
</section>
